import React, { useState, useEffect, useRef } from 'react';
import {
  Camera,
  ChefHat,
  Sparkles,
  Gift,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  ShieldCheck,
  Timer,
  BookOpen,
  ShoppingBasket,
  Flame,
  Zap,
} from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'GB' },
  { code: 'sl', name: 'Slovenščina', flag: 'SI' },
];

const content = {
  en: {
    nav: {
      features: "Features",
      howItWorks: "How it works",
      tools: "Kitchen tools",
      faq: "FAQ",
    },
    hero: {
      badge: "AI recipe assistant",
      title: "Snap a photo — get recipes that match what you have",
      subtitle:
        "Point your camera at ingredients or leftovers. DealShoppy24 uses AI to suggest recipes, timings, and smart cooking tips so you can cook with confidence.",
      cta: "Get access",
      trust: "Built for home cooks in the UK, Slovenia & beyond",
    },
    features: {
      title: "More than recipes",
      subtitle: "Handy tools that follow you from fridge to plate",
      categories: [
        "Photo → recipes",
        "Diet & allergies",
        "Timers & steps",
        "Substitutions",
        "Portions & shopping",
        "Weekly meal ideas",
      ],
      tags: [
        "Instant ideas",
        "Filters that fit you",
        "Cook-along flow",
        "Swap ingredients",
        "Lists from recipes",
        "Less food waste",
      ],
      cta: "Try the service",
    },
    howItWorks: {
      title: "Three simple steps",
      subtitle: "From a quick snap to a meal plan you can actually cook",
      steps: [
        {
          title: "Capture",
          description: "Photograph ingredients, packaging, or what is left in the fridge — no typing required.",
        },
        {
          title: "Understand",
          description: "AI reads the scene and proposes recipes ranked by fit, time, and what you already have.",
        },
        {
          title: "Cook",
          description: "Follow clear steps, built-in timers, and swaps — then save favourites for next time.",
        },
      ],
    },
    benefits: {
      title: "Cook smarter every day",
      subtitle: "Practical help for busy kitchens",
      items: [
        { title: "Always-on inspiration", description: "Turn random ingredients into doable dinners without endless searching." },
        { title: "Clear, safe guidance", description: "Highlight allergens and dietary notes so choices stay sensible for your household." },
        { title: "Faster from idea to plate", description: "Estimated prep times, shortcuts, and one-tap timers keep you on track." },
        { title: "Less waste, more flavour", description: "Use what you have first — with portion scaling and a shopping list when you do need extras." },
      ],
      phoneStat: "24/7",
      phoneStatLabel: "Recipe ideas",
    },
    popular: {
      title: "Everything in one flow",
      subtitle: "From recognition to shopping list without leaving the experience",
      bannerTitle: "Your pocket sous-chef",
      bannerSubtitle: "Photo recognition, structured recipes, and kitchen utilities in a single mobile-friendly journey.",
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          q: "How does the photo upload work?",
          a: "You take a picture in the app or mobile flow. Our AI analyses visible ingredients and context to suggest matching recipes and practical cooking notes.",
        },
        {
          q: "Do I need to list ingredients manually?",
          a: "No — that is the point. You can still refine filters (vegetarian, gluten-free, time, calories) after suggestions appear.",
        },
        {
          q: "Is this a real cooking coach?",
          a: "It is a digital assistant: it suggests recipes, steps, and tools like timers and substitutions. Always follow safe food handling and appliance instructions.",
        },
        {
          q: "Can I use it for meal planning?",
          a: "Yes. Save ideas, scale portions, and build a shopping list from the recipes you pick.",
        },
        {
          q: "How do I cancel the service?",
          a: "You can cancel via SMS at any time by sending STOP MAISTER to 6060. Full conditions are in the terms.",
        },
      ],
    },
    cta: {
      title: "Ready to cook with AI?",
      subtitle: "Get recipe ideas from your photos plus timers, swaps, and lists — start on your phone.",
      button: "Get started",
    },
    footer: {
      privacy: "Privacy",
      terms: "Terms",
      contact: "Contact",
      legal:
        "This service includes membership in Maisterchef. You will receive a personal SMS on the website or by replying; by confirming via SMS you agree to the use of the service and to receiving access to unlimited AI-generated recipes and unlimited AI culinary requests. The service price is €4.98 per week (2 SMS per week at €2.49 incl. VAT). To unsubscribe, send STOP MAISTER to 6060. With an active subscription you get unlimited access to recipes tailored to your wishes, ingredients and dietary needs, and unlimited AI requests for personalised culinary ideas. The service is charged to your mobile bill or prepaid account. Your device must be able to receive SMS and data for digital content. Maisterchef is not affiliated with or sponsored by manufacturers of products mentioned or shown in this campaign. Brands and logos belong to their owners. You must be the account holder or have the holder’s express permission; minors need parental or guardian consent. Technical business partner: NTH. For information call +386 16001850 (Mon–Fri 09:00–17:00) or write to adsaints.si@silverlines.info. Ziller Mobile B.V., Stationstraat 28, 1382 AD Weesp, Netherlands.",
    },
  },
  sl: {
    nav: {
      features: "Funkcije",
      howItWorks: "Kako deluje",
      tools: "Kuhinjski pripomočki",
      faq: "Pogosta vprašanja",
    },
    hero: {
      badge: "AI pomočnik za recepte",
      title: "Posnemite fotografijo — prejmite recepte, ki ustrezajo temu, kar imate",
      subtitle:
        "Usmerite kamero v sestavine ali ostanke. DealShoppy24 z umetno inteligenco predlaga recepte, čase in praktične kuharske nasvete, da boste kuhali z več samozavesti.",
      cta: "Pridobite dostop",
      trust: "Za domače kuharje v Združenem kraljestvu, Sloveniji in širše",
    },
    features: {
      title: "Več kot recepti",
      subtitle: "Priročna orodja od hladilnika do krožnika",
      categories: [
        "Fotografija → recepti",
        "Prehrana in alergije",
        "Časovniki in koraki",
        "Zamenjave",
        "Porcije in nakup",
        "Tedenski jedilniki",
      ],
      tags: [
        "Takojšnje ideje",
        "Filtri po vaši meri",
        "Korak za korakom",
        "Zamenjava sestavin",
        "Seznami iz receptov",
        "Manj zavržen hrane",
      ],
      cta: "Preizkusite storitev",
    },
    howItWorks: {
      title: "Tri preprosti koraki",
      subtitle: "Od hitrega posnetka do jedilnika, ki ga res lahko skuhamo",
      steps: [
        {
          title: "Posnemite",
          description: "Fotografirajte sestavine, embalažo ali kar ostane v hladilniku — brez tipkanja.",
        },
        {
          title: "Razumevanje",
          description: "Umetna inteligenca prebere posnetek in predlaga recepte po ustreznosti, času in tem, kar že imate.",
        },
        {
          title: "Kuhajte",
          description: "Sledite jasnim korakom, vgrajenim časovnikom in zamenjavam — priljubljene shranite za naslednjič.",
        },
      ],
    },
    benefits: {
      title: "Kuhajte pametneje vsak dan",
      subtitle: "Praktična pomoč za zasedene kuhinje",
      items: [
        { title: "Navdih vedno na voljo", description: "Naključne sestavine spremenite v izvedljive večerje brez neskončnega iskanja." },
        { title: "Jasno in varno", description: "Poudarimo alergene in prehranske opombe, da ostanejo izbire smiselne za vašo družino." },
        { title: "Hitreje od ideje do krožnika", description: "Ocena časa priprave, bližnjice in časovniki z enim dotikom vas držijo na poti." },
        { title: "Manj odpadkov, več okusa", description: "Najprej porabite, kar imate — s prilagajanjem porcij in nakupovalnim seznamom, ko res potrebujete še kaj." },
      ],
      phoneStat: "24/7",
      phoneStatLabel: "Ideje za jedi",
    },
    popular: {
      title: "Vse v enem toku",
      subtitle: "Od prepoznavanja do nakupovalnega seznama brez zapuščanja izkušnje",
      bannerTitle: "Vaš žepni sou-chef",
      bannerSubtitle: "Prepoznavanje fotografij, strukturirani recepti in kuhinjska orodja v eni mobilni poti.",
    },
    faq: {
      title: "Pogosta vprašanja",
      items: [
        {
          q: "Kako deluje nalaganje fotografije?",
          a: "V aplikaciji ali mobilnem toku posnamete sliko. Naša umetna inteligenca analizira vidne sestavine in kontekst ter predlaga ustrezne recepte in praktične kuharske opombe.",
        },
        {
          q: "Ali moram sestavine vpisovati ročno?",
          a: "Ne — to je bistvo. Po prikazu predlogov lahko še vedno prilagodite filtre (vegetarijansko, brez glutena, čas, kalorije).",
        },
        {
          q: "Ali je to pravi kuharski trener?",
          a: "To je digitalni pomočnik: predlaga recepte, korake in orodja, kot so časovniki in zamenjave. Vedno upoštevajte varno ravnanje z živili in navodila za aparate.",
        },
        {
          q: "Ali lahko načrtujem obroke?",
          a: "Da. Shranite ideje, prilagodite porcije in sestavite nakupovalni seznam iz izbranih receptov.",
        },
        {
          q: "Kako odpovem storitev?",
          a: "Kadarkoli prek SMS: pošljite STOP MAISTER na 6060. Polni pogoji so v splošnih pogojih.",
        },
      ],
    },
    cta: {
      title: "Pripravljeni kuhati z umetno inteligenco?",
      subtitle: "Ideje za recepte iz fotografij ter časovniki, zamenjave in seznami — začnite na telefonu.",
      button: "Začnite",
    },
    footer: {
      privacy: "Zasebnost",
      terms: "Pogoji",
      contact: "Kontakt",
      legal:
        "Ta storitev vključuje včlanitev v Maisterchef. Prejeli boste osebni SMS na spletni strani ali z odgovorom in potrditvijo preko SMS se strinjate ter potrjujete uporabo storitev in prejemanje dostopa do neomejenih AI-generiranih receptov ter neomejenih AI kulinaričnih zahtevkov (AI requests). Cena storitve je 4,98 EUR na teden (2 SMS tedensko po 2,49 EUR z DDV). Od storitve se lahko odjavite s pošiljanjem STOP MAISTER na 6060. Z aktivno naročnino prejmete neomejen dostop do receptov, prilagojenih vašim željam, sestavinam in prehranskim potrebam, ter možnost oddaje neomejenega števila AI zahtevkov za personalizirane kulinarične ideje. Storitev bo zaračunana na vašem mobilnem računu ali na vašem predplačniškem računu. Vaš aparat mora imeti omogočeno sprejemanje besedilnih sporočil ter prenos podatkov za dostop do digitalnih vsebin. Podjetje Maisterchef ni povezano ali sponzorirano s strani proizvajalcev izdelkov, ki so omenjeni ali jih je mogoče videti v tej kampanji. Blagovne znamke, znamke storitev in logotipi so last njihovih izvornih lastnikov. Za uporabo storitev morate biti lastnik naročniškega razmerja ali pridobiti izrecno dovoljenje lastnika, v primeru mladoletnosti pa morate pridobiti izrecno dovoljenje staršev ali skrbnikov. Tehnični poslovni partner: NTH. Za informacije pokličite +386 16001850 (Ponedeljek–Petek 09:00–17:00) ali pišite na adsaints.si@silverlines.info. Ziller Mobile B.V., Stationstraat 28, 1382 AD, Weesp, Nizozemska.",
    },
  },
};

const categoryIcons = [Camera, Sparkles, Timer, ChefHat, ShoppingBasket, BookOpen];
const categoryColors = [
  "from-rose-500 to-pink-600",
  "from-blue-500 to-indigo-600",
  "from-amber-500 to-orange-600",
  "from-emerald-500 to-teal-600",
  "from-violet-500 to-purple-600",
  "from-cyan-500 to-sky-600",
];

function App() {
  const [lang, setLang] = useState('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const dropdownRef = useRef(null);

  const t = content[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === lang);

  return (
    <div className="min-h-screen bg-white font-body text-dark">

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-dark">
                Deal<span className="text-primary">Shoppy</span>24
              </span>
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.features}</a>
              <a href="#how-it-works" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.howItWorks}</a>
              <a href="#categories" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.tools}</a>
              <a href="#faq" className="text-dark/80 hover:text-primary transition-colors font-medium text-sm">{t.nav.faq}</a>
            </nav>

            <div className="flex items-center space-x-3">
              {/* Language Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-primary/40 transition-colors text-sm"
                >
                  <ReactCountryFlag countryCode={currentLang.flag} svg style={{ width: '18px', height: '14px' }} />
                  <span className="font-medium hidden sm:inline">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted" />
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => { setLang(l.code); setLangDropdownOpen(false); }}
                        className={`w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-light transition-colors text-sm ${
                          lang === l.code ? 'text-primary font-semibold bg-primary/5' : 'text-dark'
                        }`}
                      >
                        <ReactCountryFlag countryCode={l.flag} svg style={{ width: '20px', height: '15px' }} />
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a href="/s" className="hidden sm:inline-flex items-center bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary-dark transition-colors font-semibold text-sm shadow-sm">
                {t.hero.cta}
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-dark hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {[
                { href: "#features", label: t.nav.features },
                { href: "#how-it-works", label: t.nav.howItWorks },
                { href: "#categories", label: t.nav.tools },
                { href: "#faq", label: t.nav.faq },
              ].map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-dark hover:text-primary transition-colors font-medium rounded-lg hover:bg-light"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/s"
                className="block text-center bg-primary text-white px-4 py-3 rounded-lg font-semibold mt-2"
              >
                {t.hero.cta}
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-20 md:pt-32 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-amber-50/40 to-white" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Zap className="w-4 h-4" />
                <span>{t.hero.badge}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark mb-6 font-heading leading-tight">
                {t.hero.title}
              </h1>
              <p className="text-lg text-muted mb-8 leading-relaxed max-w-xl">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a
                  href="/s"
                  className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30"
                >
                  {t.hero.cta}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
                <div className="flex items-center space-x-2 text-sm text-muted">
                  <div className="flex -space-x-2">
                    {['🇬🇧', '🇸🇮', '🇪🇺'].map((flag, i) => (
                      <span key={i} className="w-7 h-7 rounded-full bg-light border-2 border-white flex items-center justify-center text-sm">{flag}</span>
                    ))}
                  </div>
                  <span>{t.hero.trust}</span>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&q=80"
                alt=""
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-dark">AI</p>
                    <p className="text-xs text-muted">Match</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-bold text-dark">1000+</p>
                    <p className="text-xs text-muted">Ideas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.features.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.features.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.features.categories.map((category, i) => {
              const Icon = categoryIcons[i];
              return (
                <a
                  key={i}
                  href="/s"
                  className="group relative bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-primary/20 overflow-hidden"
                >
                  <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${categoryColors[i]} opacity-10 rounded-bl-full group-hover:opacity-20 transition-opacity`} />
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${categoryColors[i]} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-dark text-lg mb-1">{category}</h3>
                  <p className="text-primary font-semibold text-sm mb-4">{t.features.tags[i]}</p>
                  <span className="inline-flex items-center text-sm font-medium text-primary group-hover:translate-x-1 transition-transform">
                    {t.features.cta} <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.howItWorks.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.howItWorks.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20" />
            {t.howItWorks.steps.map((step, i) => {
              const icons = [Camera, Sparkles, ChefHat];
              const Icon = icons[i];
              return (
                <div key={i} className="text-center relative">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary text-white rounded-2xl mb-6 text-xl font-bold shadow-lg shadow-primary/20 relative z-10">
                    {i + 1}
                  </div>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 text-primary rounded-xl mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark mb-3 font-heading">{step.title}</h3>
                  <p className="text-muted leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <a
              href="/s"
              className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-primary-dark transition-all shadow-lg shadow-primary/25"
            >
              {t.hero.cta}
              <ChevronRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Image Feature Section */}
      <section className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1556910096-6f5e72db6803?w=900&q=80"
                alt=""
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
              <div className="absolute -bottom-4 -right-4 bg-secondary text-white p-4 rounded-xl shadow-lg">
                <p className="font-bold text-2xl">{t.benefits.phoneStat}</p>
                <p className="text-sm text-white/80">{t.benefits.phoneStatLabel}</p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 font-heading">{t.benefits.title}</h2>
              <p className="text-lg text-muted mb-8">{t.benefits.subtitle}</p>
              <div className="space-y-6">
                {t.benefits.items.map((item, i) => {
                  const icons = [Flame, ShieldCheck, Timer, ShoppingBasket];
                  const Icon = icons[i];
                  return (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-dark mb-1 font-heading">{item.title}</h3>
                        <p className="text-muted text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-8">
                <a
                  href="/s"
                  className="inline-flex items-center bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-all shadow-sm"
                >
                  {t.cta.button}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Categories Banner */}
      <section id="categories" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.popular.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.popular.subtitle}</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1400&q=80"
              alt=""
              className="w-full h-64 md:h-80 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/80 via-dark/50 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-8 md:px-12 max-w-lg">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-heading">
                  {t.popular.bannerTitle}
                </h3>
                <p className="text-white/80 mb-6">
                  {t.popular.bannerSubtitle}
                </p>
                <a
                  href="/s"
                  className="inline-flex items-center bg-white text-dark px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                >
                  {t.hero.cta}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.faq.title}</h2>
          </div>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left"
                >
                  <span className="font-semibold text-dark pr-4">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-muted flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-muted leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Gift className="w-12 h-12 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">{t.cta.title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">{t.cta.subtitle}</p>
          <a
            href="/s"
            className="inline-flex items-center bg-white text-primary px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl"
          >
            {t.cta.button}
            <ChevronRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-2 mb-6">
              <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                <ChefHat className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-lg">
                Deal<span className="text-primary">Shoppy</span>24
              </span>
            </div>
            <div className="flex justify-center space-x-6 mb-6">
              <a href="/privacy.html" className="text-gray-400 hover:text-white transition-colors text-sm">{t.footer.privacy}</a>
              <span className="text-gray-600">|</span>
              <a href="/terms.html" className="text-gray-400 hover:text-white transition-colors text-sm">{t.footer.terms}</a>
              <span className="text-gray-600">|</span>
              <a href="/contact.html" className="text-gray-400 hover:text-white transition-colors text-sm">{t.footer.contact}</a>
            </div>
            <p className="text-gray-500 text-sm mb-8">
              © {new Date().getFullYear()} DealShoppy24. All rights reserved.
            </p>

            <div className="border-t border-gray-800 pt-8 mt-8">
              <div className="max-w-4xl mx-auto">
                <p className="text-xs text-gray-600 leading-relaxed text-left">
                  {t.footer.legal}
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
